import { getExcelVersion } from "../../util/versioning";

export interface RunMark {
  label: string;
  at: number;
  ms?: number;
  info?: Record<string, any>;
}

export interface MetricsRun {
  id: string;
  startedAt: string;
  ms: number | null;
  marks: RunMark[];
}

export interface SkillMetrics {
  environment: Record<string, any>;
  runs: MetricsRun[];
}

const MAX_RUNS = 20;
const MAX_MARKS = 400;

let enabled = false;

const store = new Map<string, SkillMetrics>();
const active = new Map<string, MetricsRun>();
const started = new Map<string, number>();

const r2 = (n: number): number => Math.round(n * 100) / 100;

const now = (): number => {
  try {
    return performance.now();
  }
  catch {
    return Date.now();
  }
};

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn() ?? fallback;
  }
  catch {
    return fallback;
  }
};

const collectEnvironment = (): Record<string, any> => {
  const nav: any = safe(() => navigator as any, {});
  const conn: any = nav?.connection ?? nav?.mozConnection ?? nav?.webkitConnection ?? {};
  const mem: any = safe(() => (performance as any).memory, {}) ?? {};

  const office: Record<string, any> = {
    host: null,
    platform: null,
    version: null,
    documentUrl: null,
    documentMode: null,
    contentLanguage: null,
    displayLanguage: null,
    touchEnabled: null,
    requirementSets: null
  };

  safe(() => {
    if (typeof Office === "undefined" || !Office.context) return null;

    const d: any = Office.context.diagnostics ?? {};

    office.host = d.host ?? null;
    office.platform = d.platform ?? null;
    office.version = d.version ?? null;
    office.documentUrl = (Office.context as any).document?.url ?? null;
    office.documentMode = (Office.context as any).document?.mode ?? null;
    office.contentLanguage = Office.context.contentLanguage ?? null;
    office.displayLanguage = Office.context.displayLanguage ?? null;
    office.touchEnabled = (Office.context as any).touchEnabled ?? null;

    const sets = ["ExcelApi 1.1", "ExcelApi 1.7", "ExcelApi 1.12", "ExcelApi 1.16", "ExcelApi 1.17", "CompressedFile 1.1"];

    office.requirementSets = sets.filter(
      s => safe(() => Office.context.requirements.isSetSupported(s.split(" ")[0], s.split(" ")[1]), false)
    );

    return null;
  }, null);

  return {
    capturedAt: new Date().toISOString(),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone, null),
    timezoneOffsetMinutes: safe(() => new Date().getTimezoneOffset(), null),
    office,
    excel: safe(() => getExcelVersion() as any, null),
    userAgent: nav?.userAgent ?? null,
    platform: nav?.platform ?? null,
    language: nav?.language ?? null,
    hardwareConcurrency: nav?.hardwareConcurrency ?? null,
    deviceMemoryGb: nav?.deviceMemory ?? null,
    connection: {
      effectiveType: conn?.effectiveType ?? null,
      downlinkMbps: conn?.downlink ?? null,
      rttMs: conn?.rtt ?? null,
      saveData: conn?.saveData ?? null
    },
    jsHeap: {
      usedBytes: mem?.usedJSHeapSize ?? null,
      totalBytes: mem?.totalJSHeapSize ?? null,
      limitBytes: mem?.jsHeapSizeLimit ?? null
    },
    window: {
      innerWidth: safe(() => window.innerWidth, null),
      innerHeight: safe(() => window.innerHeight, null),
      devicePixelRatio: safe(() => window.devicePixelRatio, null),
      href: safe(() => window.location.href, null)
    }
  };
};

export const setRunMetricsEnabled = (v: boolean): void => {
  if (enabled === v) return;

  enabled = v;

  if (!v) {
    store.clear();
    active.clear();
    started.clear();
  }
};

export const runMetricsEnabled = (): boolean => enabled;

export const startRun = (skill?: string): boolean => {
  if (!enabled || !skill) return false;

  const entry = store.get(skill) ?? { environment: collectEnvironment(), runs: [] };

  const run: MetricsRun = {
    id: `run_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    startedAt: new Date().toISOString(),
    ms: null,
    marks: []
  };

  entry.runs.push(run);

  if (entry.runs.length > MAX_RUNS) {
    entry.runs.splice(0, entry.runs.length - MAX_RUNS);
  }

  store.set(skill, entry);
  active.set(skill, run);
  started.set(skill, now());

  return true;
};

export const ensureRun = (skill?: string): boolean => {
  if (!enabled || !skill) return false;
  if (active.has(skill)) return false;
  return startRun(skill);
};

export const endRun = (skill?: string): void => {
  if (!enabled || !skill) return;

  const run = active.get(skill);
  const from = started.get(skill);

  if (run && typeof from === "number") {
    run.ms = r2(now() - from);
  }

  active.delete(skill);
  started.delete(skill);
};

export const mark = (skill?: string, label?: string, info?: Record<string, any>): void => {
  if (!enabled || !skill || !label) return;

  const run = active.get(skill);
  if (!run || run.marks.length >= MAX_MARKS) return;

  const from = started.get(skill);

  run.marks.push({
    label,
    at: r2(typeof from === "number" ? now() - from : now()),
    ...(info ? { info } : {})
  });
};

export const timer = (skill?: string, label?: string) => {
  const from = enabled ? now() : 0;

  return (info?: Record<string, any>): void => {
    if (!enabled || !skill || !label) return;

    const run = active.get(skill);
    if (!run || run.marks.length >= MAX_MARKS) return;

    const runFrom = started.get(skill);
    const t = now();

    run.marks.push({
      label,
      at: r2(typeof runFrom === "number" ? t - runFrom : t),
      ms: r2(t - from),
      ...(info ? { info } : {})
    });
  };
};

export const getSkillMetrics = (skill?: string): SkillMetrics | null => {
  if (!skill) return null;
  return store.get(skill) ?? null;
};

export const clearSkillMetrics = (skill?: string): void => {
  if (!skill) return;

  store.delete(skill);
  active.delete(skill);
  started.delete(skill);
};
